import {SignalingTransport} from "../shared/services/signaling-transport";
import {Observable, Subject, Subscription, takeUntil, throwError} from "rxjs";
import {WebRtcSignaling, WebRtcSignalingContent, WebRtcSignalingKind} from "../shared/models/webrtc/web-rtc-signaling";
import {IMqttSignalingClient} from "./interfaces/IMqttSignalingClient";

export class MqttSignalingClient implements IMqttSignalingClient {

    private signalingMessagesSubscription: Subscription | null = null;

    private readonly sdpAnswer$ = new Subject<WebRtcSignalingContent>();
    private readonly iceCandidate$ = new Subject<WebRtcSignalingContent>();
    private closed$ = new Subject<void>();

    constructor(private readonly signalingTransport: SignalingTransport, private readonly clientId: string,
                private readonly installationId: string, private readonly unitId: string,
                private readonly deviceId: string, private readonly sessionClientId: string) {
    }

    open(): void {
        if (this.signalingMessagesSubscription != null) return;

        this.signalingMessagesSubscription = this.signalingTransport.subscribeToSignalingMessage(this.deviceId, this.installationId, this.unitId, this.sessionClientId).subscribe({
            next: message => {
                if (message.kind === WebRtcSignalingKind.answer) {
                    this.sdpAnswer$.next(message.content);
                } else if (message.kind === WebRtcSignalingKind.ice) {
                    this.iceCandidate$.next(message.content);
                }
            },
            error: err => console.error("Signaling error: ", err)
        });
    }

    sendIceCandidate(candidate: RTCIceCandidate): void {
        if (!candidate.candidate) {
            return;
        }
        const message: WebRtcSignaling = {
            kind: WebRtcSignalingKind.ice,
            clientId: this.clientId,
            content: {
                sdpMLineIndex: candidate.sdpMLineIndex,
                sdpMid: candidate.sdpMid,
                candidate: candidate.candidate,
            }
        };
        this.signalingTransport.sendSignalingMessage(this.deviceId, this.installationId, this.unitId, this.sessionClientId, message);
    }

    sendOffer(sdpOffer: RTCSessionDescription): void {
        if (!sdpOffer) {
            return;
        }
        const message: WebRtcSignaling = {
            kind: WebRtcSignalingKind.offer,
            clientId: this.clientId,
            content: {
                type: sdpOffer.type,
                sdp: sdpOffer.sdp
            }
        };
        this.signalingTransport.sendSignalingMessage(this.deviceId, this.installationId, this.unitId, this.sessionClientId, message);
    }

    sendAnswer(sdpAnswer: RTCSessionDescription): void {
        throw new Error("Not supported.");
    }

    onSdpOffer = (): Observable<WebRtcSignalingContent> =>
        throwError(() => new Error("Event not supported: onSdpOffer"));

    onSdpAnswer = (): Observable<WebRtcSignalingContent> =>
        this.sdpAnswer$.asObservable().pipe(takeUntil(this.closed$));

    onIceCandidate = (): Observable<WebRtcSignalingContent> =>
        this.iceCandidate$.asObservable().pipe(takeUntil(this.closed$));

    close(): void {
        this.signalingMessagesSubscription?.unsubscribe();
        this.signalingMessagesSubscription = null;
        this.closed$.next();
        this.closed$.complete();
    }

}
